import {
  APIComponentAttributes,
  APIFieldType,
  NUMBER_FORMAT_PERCENTAGE,
  ReportColumnType,
  SortOrder,
} from '@ardoq/api-types';
import {
  CellClassParams,
  CellClickedEvent,
  CellStyle,
  ColGroupDef,
  HeaderValueGetterParams,
  KeyCreatorParams,
  RowDragCallbackParams,
  SetFilterValuesFuncParams,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { uniq } from 'lodash';
import { localeCompare } from '@ardoq/locale';
import {
  columnToCellEditor,
  isCellEditorPopup,
} from '../CellEditors/cellEditors';
import { mapColumnToAgGridCellRendererIncludingHierarchies } from '../CellRenderers/cellRenderers';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';
import {
  agGridFilterOperations,
  hasValueSelectOption,
  noValueSelectOption,
} from '../filters/agGridFilterOperations';
import { HasValueFilter } from '../filters/HasValueFilter';
import { ParentComponentFilter } from '../filters/ParentFilter';
import { ReferencedComponentsFilter } from '../filters/ReferenceFilter';
import {
  AgGridPredefinedCellDataType,
  ColumnDefinitionArdoqMetadata,
  InventoryColumnDef,
  InventoryColumnDefinition,
  InventoryColumnDefinitionWithOptionList,
  InventoryColumnDefinitionWithSelectionState,
  InventoryContext,
  InventoryDatasourceSelection,
} from '../types';
import {
  toValueFormatter,
  toValueGetter,
  toValueParser,
  toValueSetter,
} from '../valueHandlers';
import { columnOperations } from './columnOperations';
import { cellRendererPropsOperations } from '../CellRenderers/cellRendererPropsOperations';
import { colors } from '@ardoq/design-tokens';
import { workspaceUtils } from '@ardoq/scope-data';
import { ConfidentialFieldHeader } from './ConfidentialFieldHeader';
import { CalculatedFieldHeader } from './CalculatedFieldHeader';
import { NameHeaderWithHierarchyControls } from './NameHeaderWithHierarchyControls';
import { t } from '@ardoq/i18n';

export type SharedColumnMappingParams = {
  datasourceSelection: InventoryDatasourceSelection;
  isHierarchyEnabled: boolean;
  fixedFilterColumnKeys?: ReadonlyArray<string>;
};

type MapReportColumnDefinitionToAgGridFormatParams =
  SharedColumnMappingParams & {
    reportColumn: InventoryColumnDefinitionWithSelectionState;
  };

const getHeaderComponentParams = (
  reportColumn: InventoryColumnDefinitionWithSelectionState,
  isHierarchyEnabled: boolean
): Pick<
  InventoryColumnDef,
  'rowDrag' | 'headerComponentParams' | 'suppressHeaderKeyboardEvent'
> => {
  if (reportColumn.key === 'name') {
    return {
      rowDrag: canDragRows,
      headerComponentParams: isHierarchyEnabled
        ? { innerHeaderComponent: NameHeaderWithHierarchyControls }
        : undefined,
      suppressHeaderKeyboardEvent:
        columnOperations.suppressHeaderKeyboardEventForInnerNavigation,
    };
  }
  if (reportColumn.isConfidential) {
    return {
      headerComponentParams: {
        innerHeaderComponent: ConfidentialFieldHeader,
      },
    };
  }
  if (reportColumn.isCalculatedField) {
    return {
      headerComponentParams: {
        innerHeaderComponent: CalculatedFieldHeader,
      },
    };
  }
  return {};
};

const getTooltipOverride = (
  reportColumn: InventoryColumnDefinitionWithSelectionState
): Pick<InventoryColumnDef, 'tooltipValueGetter'> =>
  columnOperations.shouldSuppressCellTooltip(reportColumn)
    ? { tooltipValueGetter: () => undefined }
    : {};

const getAutoHeight = (
  reportColumn: InventoryColumnDefinitionWithSelectionState
): boolean =>
  reportColumn.isVisible &&
  (columnOperations.isReferenceColumn(reportColumn) ||
    columnOperations.isLucidchartColumn(reportColumn));

const getMinWidth = (
  reportColumn: InventoryColumnDefinitionWithSelectionState
): number | undefined => {
  if (columnOperations.isReferenceColumn(reportColumn)) {
    return 200;
  }
  if (columnOperations.isParentColumn(reportColumn)) {
    return 200;
  }
  return undefined;
};

export const mapReportColumnDefinitionToAgGridFormatIgnoringHierarchy = (
  reportColumn: InventoryColumnDefinitionWithSelectionState,
  datasourceSelection: InventoryDatasourceSelection
): InventoryColumnDef => {
  return mapReportColumnDefinitionToAgGridFormat({
    reportColumn,
    datasourceSelection,
    isHierarchyEnabled: false,
  });
};

export const mapReportColumnDefinitionToAgGridFormat = ({
  reportColumn,
  datasourceSelection,
  isHierarchyEnabled,
  fixedFilterColumnKeys,
}: MapReportColumnDefinitionToAgGridFormatParams): InventoryColumnDef => {
  if (reportColumn.isExtraColumn) {
    return {
      colId: columnOperations.getColumnId(reportColumn),
      headerName: reportColumn.label,
      headerTooltip: reportColumn.columnTooltip || undefined,
      sortable: false,
      editable: false,
      filter: false,
      hide: !reportColumn.isVisible,
      pinned: reportColumn.pinned,
      cellRenderer: reportColumn.customCellRendererName,
      context: { isExtraColumn: true },
    };
  }
  const isSortable = columnOperations.isColumnSortable(
    reportColumn,
    isHierarchyEnabled
  );
  return {
    colId: columnOperations.getColumnId(reportColumn),
    field: reportColumn.key,
    headerName: reportColumn.label,
    headerTooltip: reportColumn.columnTooltip || undefined,
    headerValueGetter: (
      params: HeaderValueGetterParams<
        APIComponentAttributes,
        unknown,
        InventoryContext
      >
    ) => {
      const baseLabel = columnOperations.getColumnHeaderLabel(reportColumn);
      if (params.location !== 'columnToolPanel') return baseLabel;
      if (!reportColumn.isCalculatedField) return baseLabel;
      // AG Grid external-lib callback: read locale from context per invocation.
      const i18nLocale = params.context?.i18nLocale ?? {};
      return `${baseLabel} ${t(
        i18nLocale,
        'inventoryApp:columnChooser.calculatedSuffix',
        '(Calculated)'
      )}`;
    },
    sortable: isSortable,
    hide: !reportColumn.isVisible,
    pinned: reportColumn.pinned,
    initialSort: isSortable ? toAgGridSort(reportColumn.sort) : null,
    lockVisible: reportColumn.key === 'name',
    suppressColumnsToolPanel: reportColumn.key === 'name',
    ...getOnCellClickedCallbackOnlyForNotPersistedComponent(reportColumn),
    ...mapColumnToAgGridCellRendererIncludingHierarchies(
      reportColumn,
      isHierarchyEnabled
    ),
    cellDataType: mapReportDataTypeToAgGridDataType(reportColumn),
    cellEditor: columnToCellEditor(reportColumn),
    cellEditorPopup: isCellEditorPopup(reportColumn),
    valueFormatter: toValueFormatter(reportColumn),
    valueParser: toValueParser(reportColumn),
    valueGetter: toValueGetter(reportColumn),
    valueSetter: toValueSetter(reportColumn),
    autoHeight: getAutoHeight(reportColumn),
    minWidth: getMinWidth(reportColumn),
    editable: params => columnOperations.getIsCellEditable(params),
    context: mapReportColumnToAgGridColumnContext(reportColumn),
    ...getFilterForColumn(
      reportColumn,
      datasourceSelection,
      fixedFilterColumnKeys
    ),
    ...getHeaderComponentParams(reportColumn, isHierarchyEnabled),
    ...getTooltipOverride(reportColumn),
    suppressFillHandle: !columnOperations.canColumnUseFillHandle(reportColumn),
    cellClass: params => {
      const editableOrReadonly = columnOperations.getIsCellEditable(params)
        ? 'cell-editable'
        : 'cell-readonly';
      const isHierarchyEnabled = (params.context as InventoryContext).hierarchy
        .isHierarchyEnabled;
      const skipHierarchySpace = isHierarchyEnabled
        ? ''
        : 'skip-hierarchy-space';
      // Editors that hold several values at once (select-multiple-list, tags)
      // grow taller than the row as values are added, so the cell has to let
      // them overflow while editing.
      const multiValueEditorClass =
        params.colDef.context.reportDataType ===
          APIFieldType.SELECT_MULTIPLE_LIST ||
        columnOperations.isTagsColumn(reportColumn)
          ? 'cell-select-multiple-list'
          : '';
      return [editableOrReadonly, skipHierarchySpace, multiValueEditorClass];
    },
    cellStyle: getCellStyle(reportColumn),
    suppressKeyboardEvent:
      columnOperations.getShouldSuppressKeyBoardEvent(reportColumn),
  };
};

const canDragRows = (
  params: RowDragCallbackParams<
    APIComponentAttributes,
    string,
    InventoryContext
  >
): boolean => {
  if (!params.context.hierarchy.isHierarchyEnabled) return false;
  const workspaceId = params.data?.rootWorkspace;
  if (!workspaceId) return false;
  if (
    !workspaceUtils.isFlexibleWorkspace(params.context.scopeData, workspaceId)
  )
    return false;
  return params.context.accessControl.canEditWorkspace(workspaceId);
};

const getCellStyle =
  (reportColumn: InventoryColumnDefinitionWithSelectionState) =>
  (params: CellClassParams): CellStyle | null | undefined => {
    // Most cell styles are in InventoryStyles.ts CSS.
    // Only conditional styles that can't be done in CSS are done here.
    if (cellRendererPropsOperations.isLockedComponent(params)) {
      return {
        color: colors.textDisabled,
      };
    }

    // The name cell is exempt from `grayscale` which
    // would strip the hover colour off its link; NameRenderer greys its icon instead.
    if (cellRendererPropsOperations.isNonMatchingAncestor(params)) {
      if (columnOperations.isNameColumn(reportColumn)) {
        return {
          color: colors.textSubtle,
        } satisfies CellStyle;
      }
      return {
        filter: 'grayscale(100%)',
        color: colors.textSubtle,
      } satisfies CellStyle;
    }
    return undefined;
  };

const toAgGridSort = (sort?: SortOrder | null): 'asc' | 'desc' | null => {
  if (!sort) return null;
  return sort === 'ASC' ? 'asc' : 'desc';
};

type FilterWithParams = Pick<
  InventoryColumnDefinition,
  'filter' | 'filterParams' | 'suppressHeaderFilterButton' | 'headerClass'
>;

export const isFilterLockedForColumn = (
  columnKey: string,
  fixedFilterColumnKeys: ReadonlyArray<string> | undefined
): boolean => fixedFilterColumnKeys?.includes(columnKey) ?? false;

const getFilterForColumn = (
  reportColumn: InventoryColumnDefinitionWithSelectionState,
  datasourceSelection: InventoryDatasourceSelection,
  fixedFilterColumnKeys: ReadonlyArray<string> | undefined
): FilterWithParams => {
  if (datasourceSelection.dataSourceType === 'report') {
    // We do not support filtering reports with agGrid.
    return { filter: false };
  }
  if (isFilterLockedForColumn(reportColumn.key, fixedFilterColumnKeys)) {
    return {
      ...mapReportColumnToAgGridFilter(reportColumn),
      suppressHeaderFilterButton: true,
      headerClass: 'locked-filter-column',
    };
  }
  return mapReportColumnToAgGridFilter(reportColumn);
};

export const toAgGridColumnDefs = (
  reportColumns: InventoryColumnDefinitionWithSelectionState[],
  shared: SharedColumnMappingParams
): (InventoryColumnDef | ColGroupDef<APIComponentAttributes>)[] => {
  const groupsById = new Map<string, ColGroupDef<APIComponentAttributes>>();
  const columnDefs: (
    | InventoryColumnDef
    | ColGroupDef<APIComponentAttributes>
  )[] = [];

  reportColumns.forEach(reportColumn => {
    const columnDef = mapReportColumnDefinitionToAgGridFormat({
      ...shared,
      reportColumn,
    });
    const { fieldGroup } = reportColumn;
    if (!fieldGroup) {
      columnDefs.push(columnDef);
      return;
    }
    const existingGroup = groupsById.get(fieldGroup.id);
    if (existingGroup) {
      existingGroup.children.push(columnDef);
      return;
    }
    const group: ColGroupDef<APIComponentAttributes> = {
      groupId: `field-group:${fieldGroup.id}`,
      headerName: fieldGroup.name,
      marryChildren: true,
      children: [columnDef],
    };
    groupsById.set(fieldGroup.id, group);
    columnDefs.push(group);
  });

  return columnDefs;
};

export const mapReportColumnToAgGridColumnContext = (
  reportColumn: InventoryColumnDefinitionWithOptionList
): ColumnDefinitionArdoqMetadata => {
  if (columnOperations.isReferenceColumn(reportColumn)) {
    return {
      reportColumnType: reportColumn.type,
      ardoqInputType: 'text',
      queryBuilderType: 'string',
      inventoryColumnType: 'reference',
      referenceTypeName: reportColumn.originalLabel ?? reportColumn.label,
      direction:
        reportColumn.type === ReportColumnType.REFERENCE_TYPE_OUTGOING
          ? 'outgoing'
          : 'incoming',
    };
  }

  if (columnOperations.isLucidchartColumn(reportColumn)) {
    return {
      reportColumnType: reportColumn.type,
      ardoqInputType: 'text',
      queryBuilderType: 'string',
      inventoryColumnType: 'lucidchart',
    };
  }

  if (
    columnOperations.isCreatedAtColumn(reportColumn) ||
    columnOperations.isLastUpdatedDateColumn(reportColumn)
  ) {
    return {
      reportColumnType: reportColumn.type,
      reportDataType: reportColumn.dataType ?? APIFieldType.DATE_TIME,
      inventoryColumnType: 'field',
      isCalculatedField: false,
      isDateRangeColumn: false,
      ardoqInputType: 'date',
      queryBuilderType: 'date',
    };
  }

  if (columnOperations.isDateRangeColumn(reportColumn)) {
    return {
      reportColumnType: reportColumn.type,
      reportDataType: reportColumn.dataType ?? APIFieldType.DATE_ONLY_RANGE,
      inventoryColumnType: 'field',
      isCalculatedField: reportColumn.isCalculatedField,
      isDateRangeColumn: reportColumn.isDateRangeColumn ?? false,
      ardoqInputType: 'date',
      queryBuilderType: 'date',
    };
  }

  if (columnOperations.isTagsColumn(reportColumn)) {
    return {
      ardoqInputType: 'select',
      inventoryColumnType: 'tag',
      queryBuilderType: 'string',
      reportColumnType: reportColumn.type,
    };
  }

  switch (reportColumn.dataType) {
    case APIFieldType.USER:
    case APIFieldType.LIST:
    case APIFieldType.SELECT_MULTIPLE_LIST:
    case APIFieldType.CHECKBOX:
      return {
        reportColumnType: reportColumn.type,
        reportDataType: reportColumn.dataType,
        inventoryColumnType: 'field',
        isCalculatedField: reportColumn.isCalculatedField,
        ardoqInputType: 'select',
        queryBuilderType:
          reportColumn.dataType === APIFieldType.CHECKBOX
            ? 'boolean'
            : 'string',
        isDateRangeColumn: false,
      };

    case APIFieldType.NUMBER:
      return {
        reportColumnType: reportColumn.type,
        reportDataType: reportColumn.dataType,
        inventoryColumnType: 'field',
        isCalculatedField: reportColumn.isCalculatedField,
        ardoqInputType: 'number',
        queryBuilderType: 'number',
        isDateRangeColumn: false,
      };

    case APIFieldType.DATE_TIME:
    case APIFieldType.DATE_ONLY:
    case APIFieldType.DATE_ONLY_RANGE:
    case APIFieldType.DATE_TIME_RANGE:
      return {
        reportColumnType: reportColumn.type,
        reportDataType: reportColumn.dataType,
        inventoryColumnType: 'field',
        isCalculatedField: reportColumn.isCalculatedField,
        isDateRangeColumn: reportColumn.isDateRangeColumn ?? false,
        ardoqInputType: 'date',
        queryBuilderType: 'date',
      };

    case APIFieldType.TEXT:
    case APIFieldType.TEXT_AREA:
    case APIFieldType.FILE:
    case APIFieldType.EMAIL:
    case APIFieldType.URL:
    case null:
    case undefined:
      return {
        reportColumnType: reportColumn.type,
        reportDataType: reportColumn.dataType ?? APIFieldType.TEXT,
        inventoryColumnType: 'field',
        isCalculatedField: reportColumn.isCalculatedField,
        ardoqInputType: 'text',
        queryBuilderType: 'string',
        isDateRangeColumn: false,
      };
    default: {
      reportColumn.dataType satisfies never;
      return {
        reportColumnType: null,
        reportDataType: APIFieldType.TEXT,
        inventoryColumnType: 'field',
        isCalculatedField: reportColumn.isCalculatedField,
        ardoqInputType: 'text',
        queryBuilderType: 'string',
        isDateRangeColumn: false,
      };
    }
  }
};

export const mapReportColumnToAgGridFilter = (
  reportColumn: InventoryColumnDefinitionWithOptionList
): FilterWithParams => {
  if (columnOperations.isTypeColumn(reportColumn)) {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        suppressSorting: true,
        values: reportColumn.options.map(({ value }) => value),
        debounceMs: 1000,
      },
    };
  }
  if (columnOperations.isTagsColumn(reportColumn)) {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        suppressSorting: true,
        // Read the available tags from the live context rather than the column
        // definition: tags load asynchronously after the columns are built and
        // can be added during the modelling session, so we refresh the values
        // each time the filter opens.
        refreshValuesOnOpen: true,
        values: (params: SetFilterValuesFuncParams<APIComponentAttributes>) => {
          const { tagsByWorkspaceId, config } =
            params.context as InventoryContext;
          const tagNames = uniq(
            Object.values(tagsByWorkspaceId)
              .flat()
              .map(tag => tag.name)
          ).sort((a, b) => localeCompare(a, b, config.locale));
          params.success([
            noValueSelectOption,
            hasValueSelectOption,
            ...tagNames,
          ]);
        },
        debounceMs: 1000,
      },
    };
  }

  if (columnOperations.isReferenceColumn(reportColumn)) {
    return {
      filter: ReferencedComponentsFilter,
      filterParams: {},
    };
  }

  if (columnOperations.isWorkspaceColumn(reportColumn)) {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        debounceMs: 1000,
        suppressSorting: true,
        values: reportColumn.options,
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value.label;
        },
        keyCreator: (params: KeyCreatorParams) => {
          return params.value.value;
        },
      },
    };
  }

  if (columnOperations.isNameColumn(reportColumn)) {
    return {
      filter: 'agTextColumnFilter',
      filterParams: {
        debounceMs: 500,
        filterOptions: ['contains', 'equals'],
        defaultOption: 'contains',
        maxNumConditions: 1,
      },
    };
  }

  if (
    columnOperations.isIdColumn(reportColumn) ||
    columnOperations.isComponentKeyColumn(reportColumn)
  ) {
    return {
      filter: 'agTextColumnFilter',
      filterParams: {
        debounceMs: 500,
        filterOptions: ['equals', 'notEqual'],
        defaultOption: 'equals',
        maxNumConditions: 1,
      },
    };
  }

  if (columnOperations.isDescriptionColumn(reportColumn)) {
    return {
      filter: 'agTextColumnFilter',
      filterParams: {
        debounceMs: 500,
        filterOptions: ['contains', 'notContains', 'blank', 'notBlank'],
        buttons: ['reset'],
        defaultOption: 'contains',
        maxNumConditions: 1,
      },
    };
  }

  if (
    columnOperations.isCreatedAtColumn(reportColumn) ||
    columnOperations.isLastUpdatedDateColumn(reportColumn)
  ) {
    return {
      filter: 'agDateColumnFilter',
      filterParams: {
        debounceMs: 500,
        maxNumConditions: 1,
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
        buttons: ['reset'],
      },
    };
  }
  if (columnOperations.isParentColumn(reportColumn)) {
    return {
      filter: ParentComponentFilter,
      filterParams: {},
    };
  }

  if (columnOperations.isLucidchartColumn(reportColumn)) {
    return {
      filter: HasValueFilter,
      filterParams: {},
    };
  }

  if (columnOperations.isDateRangeColumn(reportColumn)) {
    return {
      filter: 'agDateColumnFilter',
      filterParams: {
        debounceMs: 500,
        maxNumConditions: 1,
        buttons: ['reset'],
        filterOptions: [
          'lessThan',
          'greaterThan',
          'inRange',
          'blank',
          'notBlank',
        ],
      },
    };
  }
  // Filtering based on provenance columns will be supported in future
  if (columnOperations.isProvenanceColumn(reportColumn)) {
    return agGridFilterOperations.getDisabledFilter();
  }

  switch (reportColumn.dataType) {
    case APIFieldType.USER: {
      return {
        filter: 'agSetColumnFilter',
        filterParams: {
          debounceMs: 1000,
          suppressSorting: true,
          values: [
            ...reportColumn.options,
            { label: noValueSelectOption, value: noValueSelectOption },
          ],
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value.label;
          },
          keyCreator: (params: KeyCreatorParams) => {
            return params.value.value;
          },
        },
      };
    }

    case APIFieldType.TEXT:
    case APIFieldType.TEXT_AREA:
    case APIFieldType.FILE:
    case APIFieldType.EMAIL:
      return {
        filter: 'agTextColumnFilter',
        filterParams: {
          debounceMs: 500,
          filterOptions: [
            'contains',
            'notContains',
            'blank',
            'notBlank',
            'equals',
            'notEqual',
          ],
          defaultOption: 'contains',
          buttons: ['reset'],
          maxNumConditions: 1,
        },
      };
    case APIFieldType.URL:
      return {
        filter: 'agTextColumnFilter',
        filterParams: {
          debounceMs: 500,
          filterOptions: ['contains', 'notContains', 'blank', 'notBlank'],
          buttons: ['reset'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
      };
    case APIFieldType.NUMBER:
      return {
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: [
            'equals',
            'notEqual',
            'blank',
            'notBlank',
            'greaterThan',
            'lessThan',
          ],
          debounceMs: 500,
          buttons: ['reset'],
          defaultOption: 'equals',
          maxNumConditions: 1,
          ...(reportColumn.numberFormatOptions === NUMBER_FORMAT_PERCENTAGE
            ? {
                numberParser: (text: string | null): number | null => {
                  if (text === null || text === '') return null;
                  const num = parseFloat(text);
                  if (isNaN(num)) return null;
                  // Convert user input (25) to stored value (0.25)
                  return num / 100;
                },
              }
            : {}),
        },
      };

    case APIFieldType.CHECKBOX:
      return {
        filter: 'agSetColumnFilter',
        filterParams: {
          suppressSorting: true,
          debounceMs: 1000,
          values: [true, false, noValueSelectOption],
          valueFormatter: (
            params: ValueFormatterParams<
              APIComponentAttributes,
              unknown,
              InventoryContext
            >
          ) => {
            // AG Grid external-lib callback: read locale from context per
            // invocation; refreshCells({ force: true }) re-runs the formatter.
            const i18nLocale = params.context?.i18nLocale ?? {};
            if (params.value === true) {
              return t(
                i18nLocale,
                'inventoryApp:ag.checkboxFilter.checked',
                'Checked'
              );
            }
            if (params.value === false) {
              return t(
                i18nLocale,
                'inventoryApp:ag.checkboxFilter.unchecked',
                'Unchecked'
              );
            }
            return t(
              i18nLocale,
              'inventoryApp:ag.checkboxFilter.notSet',
              'Not set'
            );
          },
        },
      };

    case APIFieldType.DATE_TIME:
    case APIFieldType.DATE_ONLY:
      return {
        filter: 'agDateColumnFilter',
        filterParams: {
          buttons: ['reset'],
          maxNumConditions: 1,
          debounceMs: 500,
        },
      };
    case APIFieldType.LIST:
    case APIFieldType.SELECT_MULTIPLE_LIST:
      return {
        filter: 'agSetColumnFilter',
        filterParams: {
          debounceMs: 1000,
          suppressSorting: true,
          values: [
            ...reportColumn.options.map(({ value }) => value),
            noValueSelectOption,
          ],
        },
      };

    case null:
    case undefined:
    case APIFieldType.DATE_ONLY_RANGE:
    case APIFieldType.DATE_TIME_RANGE:
      return agGridFilterOperations.getDisabledFilter();
    default:
      reportColumn.dataType satisfies never;
      return agGridFilterOperations.getDisabledFilter();
  }
};

const mapReportDataTypeToAgGridDataType = (
  column: InventoryColumnDefinitionWithSelectionState
): AgGridPredefinedCellDataType | false => {
  if (columnOperations.isReferenceColumn(column)) {
    // change to object and add formatter/parser
    // if we want to support copy paste etc for reference columns
    return false;
  }
  if (
    columnOperations.isLucidchartColumn(column) ||
    columnOperations.isProvenanceColumn(column)
  ) {
    return false;
  }
  switch (column.dataType) {
    case APIFieldType.TEXT:
    case APIFieldType.TEXT_AREA:
    case APIFieldType.EMAIL:
      return 'text';

    case APIFieldType.NUMBER:
      return 'number';

    case APIFieldType.CHECKBOX:
      return 'boolean';

    case APIFieldType.DATE_ONLY:
    case APIFieldType.DATE_TIME:
      return column.isDateRangeColumn ? 'object' : 'dateString';
    case APIFieldType.SELECT_MULTIPLE_LIST:
    case APIFieldType.DATE_ONLY_RANGE:
    case APIFieldType.DATE_TIME_RANGE:
      return 'object';
    case APIFieldType.LIST:
    case APIFieldType.FILE:
    case APIFieldType.URL:
    case APIFieldType.USER:
      return 'text';
    case null:
    case undefined:
      return 'text';
    default:
      column.dataType satisfies unknown;
      return 'text';
  }
};

const getOnCellClickedCallbackOnlyForNotPersistedComponent = (
  reportColumn: InventoryColumnDefinitionWithSelectionState
) => {
  return columnOperations.isTypeColumn(reportColumn) ||
    columnOperations.isWorkspaceColumn(reportColumn)
    ? {
        onCellClicked: (
          params: CellClickedEvent<APIComponentAttributes, InventoryContext>
        ) => {
          if (
            createComponentsOperations.isComponentNotPersisted(params.data!._id)
          )
            // These cells already look like they are in edit mode.So we set the cell to edit mode on click here so that the user doesn't have to double click
            params.api.startEditingCell({
              rowPinned: 'top',
              rowIndex: params.rowIndex ?? 0,
              colKey: reportColumn.key!,
            });
        },
      }
    : {};
};
